/**
 * 소모품 카드 — 품명·수량·비고 세 칸짜리 표 한 장을 보고, 권한이 있으면 고쳐 저장한다.
 *
 * 회사 전체(projectId = 0)와 현장 카드가 같은 카드를 쓴다. 다른 건 수량 칸 이름
 * 뿐이다 — 회사는 "재고 수량", 현장은 "사용중". 두 값을 품명으로 맞춰 봐야 "창고에
 * 몇 개 남았고 현장에 몇 개 나가 있는지" 가 나오므로, 현장 카드에는 회사 품목을
 * 품명 추천 목록으로 넘겨 표기가 갈리지 않게 한다.
 *
 * 표는 기존 메모 `content` 한 칸에 JSON 으로 들어간다(consumableSheet 참고).
 * 저장은 내용 전체를 PUT 한다.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon } from '@mui/icons-material';

import { ConsumableTableEditor, ConsumableTableView } from './ConsumableTable';
import usePermissions from '../hooks/usePermissions';
import {
  ConsumableDraftRow,
  fromConsumableDraftRows,
  newConsumableDraftRow,
  parseConsumableSheet,
  serializeConsumableSheet,
  sumConsumableQty,
  toConsumableDraftRows,
} from '../services/consumableSheet';
import {
  SiteConsumableNote,
  SiteConsumableNoteService,
} from '../services/SiteConsumableNoteService';

interface Props {
  /** 0 = 회사 전체, 그 외 = 현장 프로젝트 ID. */
  projectId: number;
  title: string;
  /** 제목 아래 한 줄 설명. */
  subtitle?: string;
  /** 처음부터 접어 둔다 — 전체 소모품 화면에 현장 카드가 여럿 붙을 때. */
  defaultCollapsed?: boolean;
  /** 수량 칸 제목. */
  quantityLabel?: string;
  /** 품명 입력칸 추천 목록. */
  nameOptions?: string[];
  /** 부모가 목록 조회로 이미 받아 둔 메모. 주면 카드는 따로 조회하지 않는다. */
  note?: SiteConsumableNote;
  onSaved?: (note: SiteConsumableNote) => void;
}

function formatUpdated(note: SiteConsumableNote): string | null {
  if (!note.id || !note.updated_at) return null;
  const t = new Date(note.updated_at);
  if (Number.isNaN(t.getTime())) return null;
  const when = t.toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' });
  return note.updated_by_name ? `${note.updated_by_name} · ${when}` : when;
}

const ConsumableNoteCard: React.FC<Props> = ({
  projectId,
  title,
  subtitle,
  defaultCollapsed = false,
  quantityLabel = '수량',
  nameOptions,
  note: controlledNote,
  onSaved,
}) => {
  const { canEdit } = usePermissions();
  const [fetchedNote, setFetchedNote] = useState<SiteConsumableNote | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [editing, setEditing] = useState(false);
  const [draftRows, setDraftRows] = useState<ConsumableDraftRow[]>([]);
  const [draftMemo, setDraftMemo] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const note = controlledNote ?? fetchedNote;

  useEffect(() => {
    if (controlledNote) return undefined;
    let cancelled = false;
    setLoadError(null);
    SiteConsumableNoteService.get(projectId)
      .then((n) => {
        if (!cancelled) setFetchedNote(n);
      })
      .catch((e: unknown) => {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : '소모품 조회 실패');
      });
    return () => {
      cancelled = true;
    };
    // controlledNote 는 "부모가 쥐고 있는가" 만 보면 된다 — 내용이 바뀌어도 재조회하지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, Boolean(controlledNote)]);

  const sheet = useMemo(() => parseConsumableSheet(note?.content ?? ''), [note?.content]);

  const startEdit = useCallback(() => {
    const rows = toConsumableDraftRows(sheet.items);
    setDraftRows(rows.length > 0 ? rows : [newConsumableDraftRow()]);
    setDraftMemo(sheet.memo);
    setSaveError(null);
    setCollapsed(false);
    setEditing(true);
  }, [sheet]);

  const save = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const content = serializeConsumableSheet({
        items: fromConsumableDraftRows(draftRows),
        memo: draftMemo,
      });
      const saved = await SiteConsumableNoteService.save(projectId, content);
      setFetchedNote(saved);
      onSaved?.(saved);
      setEditing(false);
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : '소모품 저장 실패');
    } finally {
      setSaving(false);
    }
  }, [projectId, draftRows, draftMemo, onSaved]);

  const updated = note ? formatUpdated(note) : null;
  const hasContent = sheet.items.length > 0 || Boolean(sheet.memo.trim());

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.75, sm: 2.5 }, borderRadius: 2 }} data-testid={`consumable-note-${projectId}`}>
      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              onClick={() => !editing && setCollapsed((c) => !c)}
              sx={{ cursor: editing ? 'default' : 'pointer', width: 'fit-content' }}
            >
              {title}
            </Typography>
            {/* 접어 둔 카드도 품목 수·합계는 보여야 펼칠지 말지 정할 수 있다.
                아직 아무것도 안 적은 현장은 그것대로 눈에 띄어야 한다. */}
            {sheet.items.length > 0 ? (
              <Chip
                size="small"
                variant="outlined"
                label={`${sheet.items.length}품목 · ${quantityLabel} ${sumConsumableQty(sheet.items)}`}
              />
            ) : (
              !hasContent && (
                <Chip size="small" variant="outlined" label="미기입" sx={{ color: 'text.disabled' }} />
              )
            )}
          </Stack>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" display="block">
              {subtitle}
            </Typography>
          )}
          {updated && (
            <Typography variant="caption" color="text.secondary" display="block">
              마지막 수정 {updated}
            </Typography>
          )}
        </Box>
        {canEdit && !editing && note && (
          <Button size="small" startIcon={<EditIcon />} onClick={startEdit} sx={{ textTransform: 'none', flexShrink: 0 }}>
            {hasContent ? '수정' : '기입'}
          </Button>
        )}
        {editing && (
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button size="small" onClick={() => setEditing(false)} disabled={saving} sx={{ textTransform: 'none' }}>
              취소
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
              onClick={save}
              disabled={saving}
              disableElevation
              sx={{ textTransform: 'none' }}
            >
              저장
            </Button>
          </Stack>
        )}
      </Stack>

      {loadError && <Alert severity="error">{loadError}</Alert>}
      {saveError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {saveError}
        </Alert>
      )}

      {!note && !loadError && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {note && editing && (
        <Stack spacing={1.5}>
          <ConsumableTableEditor
            rows={draftRows}
            quantityLabel={quantityLabel}
            onChange={setDraftRows}
            nameOptions={nameOptions}
            nameListId={`consumable-names-${projectId}`}
          />
          {/* 표를 쓰기 전 자유 서식으로 적어 둔 내용. 표로 옮기고 비우면 다시 뜨지 않는다. */}
          {draftMemo.trim() && (
            <TextField
              label="이전 자유 메모 (표로 옮긴 뒤 비워 주세요)"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={draftMemo}
              onChange={(e) => setDraftMemo(e.target.value)}
            />
          )}
        </Stack>
      )}

      {note && !editing && !collapsed && (
        <Stack spacing={1.5}>
          <ConsumableTableView
            rows={sheet.items}
            quantityLabel={quantityLabel}
            emptyText="아직 기입된 소모품이 없습니다."
          />
          {sheet.memo.trim() && (
            <Box sx={{ p: 1.25, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block" fontWeight={700}>
                이전 자유 메모
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {sheet.memo}
              </Typography>
            </Box>
          )}
        </Stack>
      )}
    </Paper>
  );
};

export default ConsumableNoteCard;
